import parsePhoneNumberFromString from 'libphonenumber-js';
import { countries } from '../constants/countres';
import { refreshAxiosCache } from './axios';

const DIAL_STORAGE_KEY = 'country-dial-code';
const ISO_STORAGE_KEY = 'country-iso';

export function dialDigitsFromPhoneCode(phoneCode) {
  return String(phoneCode || '').replace(/\D/g, '');
}

/**
 * Map ISO country_code (e.g. EG / SA) to local countries[].id
 */
export function countryIdFromCode(countryCode) {
  if (!countryCode) return null;
  const normalized = String(countryCode).trim().toLowerCase();
  const match = countries.find((c) => {
    const code = c.code.toLowerCase();
    return (
      code === normalized ||
      (code === 'sa' && ['sa', 'sau', 'ksa', 'saudi', 'saudi arabia'].includes(normalized)) ||
      (code === 'eg' && ['eg', 'egy', 'egypt'].includes(normalized))
    );
  });
  return match?.id ?? null;
}

/**
 * Detect country id from an international phone string (e.g. 9665... / +9665...).
 */
export function countryIdFromPhone(phone) {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return null;

  const sorted = [...countries].sort((a, b) => b.phone.length - a.phone.length);
  for (const c of sorted) {
    if (digits.startsWith(c.phone)) return c.id;
  }
  return null;
}

export function countryIdFromDialCode(dialCode) {
  if (!dialCode) return null;
  const match = countries.find((c) => String(c.phone) === String(dialCode));
  return match?.id ?? null;
}

/**
 * Resolve country id from store using phone / country_code first.
 * Bare numeric ids are used only when preferNumericId is true.
 */
export function resolveStoreCountryId(store, { preferNumericId = true } = {}) {
  if (!store) return null;

  const fromPhone = countryIdFromPhone(store.phone);
  if (fromPhone) return fromPhone;

  const nestedCountry = store.country;
  const code =
    nestedCountry?.country_code ??
    nestedCountry?.code ??
    store.country_code ??
    null;

  const fromCode = countryIdFromCode(code);
  if (fromCode) return fromCode;

  if (!preferNumericId) return null;

  const directId =
    nestedCountry?.id ??
    store.country_id ??
    nestedCountry?.country_id ??
    null;

  if (directId !== undefined && directId !== null && directId !== '') {
    return Number(directId) || directId;
  }

  return null;
}

/**
 * Resolves the account country id for location/city queries.
 */
export function getAccountCountryId({
  selectedCountry,
  storeCountryId,
  store,
  fallbackToStorage = true,
} = {}) {
  if (selectedCountry !== undefined && selectedCountry !== null && selectedCountry !== '') {
    return selectedCountry;
  }

  const fromStore = storeCountryId ?? resolveStoreCountryId(store);
  if (fromStore !== undefined && fromStore !== null && fromStore !== '') {
    return fromStore;
  }

  if (fallbackToStorage) {
    const stored = localStorage.getItem('country-id');
    if (stored && stored !== 'undefined' && stored !== 'null') {
      return stored;
    }
  }

  return null;
}

/**
 * ISO code (EG, BH, …) for locked phone inputs and libphonenumber placeholders.
 */
export function resolveAccountCountryIso({ store, user } = {}) {
  const nested = store?.country ?? user?.store?.country;
  const fromApi = nested?.country_code ?? nested?.code;
  if (fromApi) return String(fromApi).trim().toUpperCase();

  try {
    const stored = localStorage.getItem(ISO_STORAGE_KEY);
    if (stored && stored !== 'undefined' && stored !== 'null') {
      return String(stored).trim().toUpperCase();
    }
  } catch (_) {
    /* ignore */
  }

  const countryId = localStorage.getItem('country-id');
  const fromConstants = countries.find((c) => String(c.id) === String(countryId));
  return fromConstants?.code ? String(fromConstants.code).toUpperCase() : '';
}

/**
 * National dial digits (20, 973, …) from store/user API data, register cache, or legacy constants.
 */
export function resolveAccountDialCode({ store, user } = {}) {
  const nested = store?.country ?? user?.store?.country;
  const fromApi = dialDigitsFromPhoneCode(nested?.phone_code);
  if (fromApi) return fromApi;

  for (const phone of [store?.phone, user?.phone]) {
    if (!phone) continue;
    const raw = String(phone).trim();
    const normalized = raw.startsWith('+') ? raw : `+${raw}`;
    const parsed = parsePhoneNumberFromString(normalized);
    if (parsed?.countryCallingCode) {
      return String(parsed.countryCallingCode);
    }
  }

  try {
    const storedDial = localStorage.getItem(DIAL_STORAGE_KEY);
    if (storedDial && storedDial !== 'undefined' && storedDial !== 'null') {
      const digits = dialDigitsFromPhoneCode(storedDial);
      if (digits) return digits;
    }
  } catch (_) {
    /* ignore */
  }

  const countryId = localStorage.getItem('country-id');
  const fromConstants = countries.find((c) => String(c.id) === String(countryId));
  if (fromConstants) return fromConstants.phone;

  return countries[0].phone;
}

/** Dial code for locked phone inputs (no store/user in scope). */
export function getAccountDialCode() {
  return resolveAccountDialCode();
}

/** Keep dial + ISO alongside country-id after register or store load. */
export function syncAccountPhoneCountryMeta(countryOrStore) {
  const country =
    countryOrStore && typeof countryOrStore === 'object' && countryOrStore.country
      ? countryOrStore.country
      : countryOrStore;
  if (!country || typeof country !== 'object') return;

  const dial = dialDigitsFromPhoneCode(country.phone_code);
  const iso = country.country_code ?? country.code;
  try {
    if (dial) localStorage.setItem(DIAL_STORAGE_KEY, dial);
    if (iso) localStorage.setItem(ISO_STORAGE_KEY, String(iso).toUpperCase());
  } catch (_) {
    /* ignore */
  }
}

export function syncRegisterPhoneCountryMeta({ dial, iso }) {
  try {
    if (dial) localStorage.setItem(DIAL_STORAGE_KEY, dialDigitsFromPhoneCode(dial));
    if (iso) localStorage.setItem(ISO_STORAGE_KEY, String(iso).toUpperCase());
  } catch (_) {
    /* ignore */
  }
}

/**
 * Persist country-id and refresh axios header cache.
 */
export function syncCountryId(countryId) {
  if (countryId === undefined || countryId === null || countryId === '') {
    return;
  }
  localStorage.setItem('country-id', String(countryId));
  refreshAxiosCache();
}
